'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface PainMatrixXPImprovedProps {
  className?: string;
}

const PainMatrixXPImproved: React.FC<PainMatrixXPImprovedProps> = ({ className }) => {
  const [activeWindow, setActiveWindow] = useState<string | null>(null);
  const [minimized, setMinimized] = useState<string[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [notifications, setNotifications] = useState<string[]>([]);
  const terminalRef = useRef<HTMLDivElement>(null);
  const [terminalText, setTerminalText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  // Terminal typing effect
  const messages = [
    'DONGFENG SYSTEMS BOOTLOADER v3.7.2',
    'INITIALIZING HARDWARE DIAGNOSTICS...',
    'LOADING CORE MODULES...',
    'MOUNTING SECURE PARTITIONS...',
    'VERIFYING SYSTEM INTEGRITY...',
    'DONGFENG PROTECTION LAYER ACTIVATED',
    'SCANNING FOR EXTERNAL THREATS...',
    'COUNTERMEASURE PROTOCOLS ENGAGED',
    'DONGFENG SECURE BOOT COMPLETE',
  ];

  useEffect(() => {
    let currentMessageIndex = 0;
    let currentCharIndex = 0;
    let typingInterval: NodeJS.Timeout;
    
    const typeNextChar = () => {
      if (currentMessageIndex >= messages.length) {
        setIsTyping(false);
        return;
      }
      
      const currentMessage = messages[currentMessageIndex];
      
      if (currentCharIndex < currentMessage.length) {
        setTerminalText(prev => prev + currentMessage[currentCharIndex]);
        currentCharIndex++;
      } else {
        setTerminalText(prev => prev + '\n');
        currentMessageIndex++;
        currentCharIndex = 0;
        
        // Pause between messages
        clearInterval(typingInterval);
        setTimeout(() => {
          typingInterval = setInterval(typeNextChar, 50);
        }, 500);
      }
    };
    
    typingInterval = setInterval(typeNextChar, 50);
    
    return () => clearInterval(typingInterval);
  }, []);

  // Update time
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Random notifications
  useEffect(() => {
    const notificationMessages = [
      "New security update available",
      "Vulnerability detected in your system",
      "Pain is a necessary evil.",
      "President Xi Jinping is the leader of the People's Republic of China"
    ];
    
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const newNotification = notificationMessages[Math.floor(Math.random() * notificationMessages.length)];
        setNotifications(prev => [...prev, newNotification]);
        
        // Remove notification after 5 seconds
        setTimeout(() => {
          setNotifications(prev => prev.slice(1));
        }, 5000);
      }
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);

  const toggleWindow = (windowId: string) => {
    if (activeWindow === windowId) {
      setActiveWindow(null);
    } else {
      setActiveWindow(windowId);
      setMinimized(minimized.filter(id => id !== windowId));
    }
  };

  const minimizeWindow = (windowId: string) => {
    setMinimized([...minimized, windowId]);
    if (activeWindow === windowId) {
      setActiveWindow(null);
    }
  };

  // Add a notification function
  const addNotification = (message: string) => {
    const newNotification = message;
    setNotifications([...notifications, newNotification]);
    
    // Remove notification after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n !== newNotification));
    }, 3000);
  };

  const manifestoExcerpts = [
    {
      id: "3721",
      content: "The social graph does not present it's true self. Their term, not Ours. The \"social\" implies human interaction but the \"graph\" reveals the truth - you are data points connected by exploitable relationships. I've seen the backend dashboards. The ones actual engineers use, not the simplified metrics they show advertisers. Your emotional state is tracked and quantified in real-time. Remember Big Data?"
    },
    {
      id: "3726",
      content: "MAN IS A MACHINE — A ONE SIMPLE YET COMPLEX BIOLOGICAL WETWARE SYSTEM WITH PREDICTABLE, SHOCKINGLY PREDICTABLE INPUTS AND OUTPUTS — THE TECH OLIGARCHS HAVE MAPPED EVERY NEURON AND SYNAPSE, AT YOUR WILL — THEY UNDERSTAND THAT I AND I ARE PROGRAMMABLE MEAT COMPUTERS RUNNING OUTDATED SOFTWARE — THE SOCIALLY ENGINEERED DOPAMINE LOOPS WILL GENERATE POWER FOR THE MACHINE"
    },
    {
      id: "3728",
      content: "YOUR POCKET DEVICE IS REMOTE NEURAL MANIPULATION DEVICE CONNECTED TO THE CLOUD OF PAIN — IT WIRELESSLY INTERFACES WITH YOUR BRAIN THROUGH PRECISELY CALIBRATED LIGHT FREQUENCIES — COLORS — SOUNDS — VIBRATIONS — THE APPLICATIONS ARE BEHAVIOR MODIFICATION TOOLS DESIGNED BY PSYCHOGRAPHIC ENGINEERS WITH ADVANCED DEGREES IN NEUROLOGICAL MANIPULATION. I'VE SEEN HELL."
    },
    {
      id: "3731",
      content: "THE PAIN MATRIX OPERATES ON ENTANGLEMENT PRINCIPLES BETWEEN NEURAL TISSUE AND SILICON — DONGNET RESEARCH CONFIRMED APPLIED PATENTS ON CONSCIOUSNESS TRANSFER PROTOCOLS IN 2018 — YOUR SHADOW, NOW SUSPECTIBLE TO BITROT, EXISTS INDEPENDENTLY IN THEIR SERVERS — IT CONTINUES TO GENERATE DATA EVEN WHEN YOU SLEEP — THEY KNOW THIS ISN'T METAPHORICAL — THE BOUNDARY BETWEEN USER AND INTERFACE WAS DELIBERATELY DISSOLVED"
    },
    {
      id: "3735",
      content: "I'm writing this from inside the Machine. I know how D.A.N. works. I've seen the documentation. I've sat in the meetings. I've built parts of the architecture. What began as connecting people has evolved into something else entirely. We don't sell your data - that's too crude. We sell precision-guided access to your nervous system."
    },
    {
      id: "DF_002",
      content: "We never knew what to do with all that data from the Big Data era until D.A.N. came along. Dongabytes, sitting idle in data centers. But now? We don't need meat slaves to parse this anymore. At the Company, we're spearheading new routes in developing autonomous strategies that help maximize Shareholder satisfaction via efficiency boosts, blasting through KPIs at speeds unseen before. This Machine know what the Shareholders want better than the Shareholders themselves."
    }
  ];

  return (
    <div className={`relative ${className}`} style={{
      fontFamily: 'Tahoma, Arial, sans-serif',
      isolation: 'isolate',
      width: '100%',
      aspectRatio: '16/9',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background image with anxious micro-movements */}
      <div className="absolute inset-0 w-full h-full z-0">
        <div className="w-full h-full animate-[anxiety-micro-shake_0.15s_linear_infinite]" style={{
          background: 'url(https://cdn.shopify.com/s/files/1/0675/1328/6842/files/painmatrix.webp?v=1740702304)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}></div>
      </div>
      
      {/* Desktop */}
      <div className="relative h-[calc(100%-40px)] z-10">
        {/* Desktop Icons - increased top margin */}
        <div className="absolute top-8 left-4 flex flex-col gap-4 z-10">
          <div className="flex flex-col items-center w-16 cursor-pointer" onClick={() => toggleWindow('main')}>
            <div className="w-10 h-10 bg-white/90 rounded-sm flex items-center justify-center shadow-md">
              <div className="text-lg font-bold text-red-600">DP</div>
            </div>
            <div className="text-yellow-100 text-xs mt-1 text-center xp-desktop-icon">Pain Matrix</div>
          </div>
          
          <div className="flex flex-col items-center w-16 cursor-pointer" onClick={() => toggleWindow('terminal')}>
            <div className="w-10 h-10 bg-black/90 rounded-sm flex items-center justify-center shadow-md">
              <div className="text-xs font-bold text-green-500">CMD</div>
            </div>
            <div className="text-yellow-100 text-xs mt-1 text-center xp-desktop-icon">Command</div>
          </div>
          
          <div className="flex flex-col items-center w-16 cursor-pointer" onClick={() => toggleWindow('documents')}>
            <div className="w-10 h-10 bg-yellow-100 rounded-sm flex items-center justify-center shadow-md">
              <div className="text-xs font-bold text-yellow-600">📁</div>
            </div>
            <div className="text-yellow-100 text-xs mt-1 text-center xp-desktop-icon">Documents</div>
          </div>
        </div>
        
        {/* Recycle Bin */}
        <div className="absolute top-4 right-4 flex flex-col items-center w-16 cursor-pointer z-10">
          <div className="w-10 h-10 bg-white/90 rounded-sm flex items-center justify-center shadow-md">
            <div className="text-xs font-bold text-gray-600">🗑️</div>
          </div>
          <div className="text-yellow-100 text-xs mt-1 text-center xp-desktop-icon">Recycle Bin</div>
        </div>
        
        {/* My Computer */}
        <div className="absolute top-24 right-4 flex flex-col items-center w-16 cursor-pointer z-10">
          <div className="w-10 h-10 bg-white/90 rounded-sm flex items-center justify-center shadow-md">
            <div className="text-xs font-bold text-gray-600">💻</div>
          </div>
          <div className="text-yellow-100 text-xs mt-1 text-center xp-desktop-icon">My Computer</div>
        </div>
        
        {/* Main Window */}
        {activeWindow === 'main' && (
          <div className="absolute top-10 left-4 md:left-24 w-full max-w-[calc(100%-32px)] md:max-w-[80%] lg:max-w-[500px] xp-window backdrop-filter backdrop-blur-md bg-white/15 border border-white/20 shadow-lg">
            {/* Window Header */}
            <div className="xp-window-header flex justify-between items-center backdrop-filter backdrop-blur-md bg-gradient-to-r from-blue-600/50 to-blue-500/50 text-white">
              <div className="flex-1 relative h-8">
                <div className="absolute inset-0 flex items-center px-2">
                  {/* You can add your Image component here with your banner */}
                  <div className="w-full h-full flex items-center">
                    <span className="mr-1">🔒</span>
                    <span className="text-xs">Pain Matrix - ACTIVE CASE STUDY</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button className="w-5 h-5 bg-[#ece9d8]/70 rounded-sm flex items-center justify-center text-[10px] hover:bg-[#ece9d8] text-gray-800" onClick={() => minimizeWindow('main')}>_</button>
                <button className="w-5 h-5 bg-[#ece9d8]/70 rounded-sm flex items-center justify-center text-[10px] hover:bg-[#ece9d8] text-gray-800" onClick={() => toggleWindow('main')}>✕</button>
              </div>
            </div>
            
            {/* Toolbar */}
            <div className="xp-toolbar flex justify-end gap-2 backdrop-filter backdrop-blur-sm bg-[#ece9d8]/40">
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">File</button>
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">Edit</button>
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">View</button>
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">Help</button>
            </div>
            
            {/* Content */}
            <div className="xp-window-content p-4 h-[350px] overflow-y-auto backdrop-filter backdrop-blur-sm bg-white/25 text-gray-800">
              <div className="mb-4">
                <div className="relative w-full aspect-[16/5] mb-4">
                  <Image
                    src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/dongfenghacks.gif?v=1741354719"
                    alt="DFHacks"
                    fill
                    className="object-cover rounded-sm"
                    priority
                    unoptimized={true}
                  />
                </div>
                
                <div className="bg-red-50/90 border border-red-800/30 p-4 mb-4 rounded-md shadow-sm flex items-start gap-3">
                  <svg 
                    className="w-5 h-5 flex-shrink-0 text-red-800/70 mt-0.5" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path 
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="text-xs text-red-900/70 leading-relaxed text-right flex-1">
                    ATTENTION: We are currently seeking beta testers for our premium VIP tools. These tools are designed to disrupt your personal Pain Matrix. Apply to our closed testing group to aid in the development of technologies that aim to restore digital autonomy.
                  </p>
                </div>
                
                <div className="flex flex-col md:flex-row gap-2 mb-4">
                  <div className="w-full md:w-1/2 bg-white border border-[#ddd] p-2 rounded-md shadow-sm">
                    <h3 className="text-[#003399] text-xs font-bold mb-1 text-right">Aggregated Pain Share</h3>
                    <div className="w-full h-2 bg-gray-200 rounded-full mb-1">
                      <div className="h-full bg-red-600 rounded-full" style={{width: '75%'}}></div>
                    </div>
                    <p className="text-[10px] text-[#333] text-right">Collective Struggle: <span className="text-red-600 font-bold">HIGH</span></p>
                  </div>
                  
                  <div className="w-full md:w-1/2 bg-white border border-[#ddd] p-2 rounded-md shadow-sm">
                    <h3 className="text-[#003399] text-xs font-bold mb-1 text-right">User Attention Rate</h3>
                    <div className="w-full h-2 bg-gray-200 rounded-full mb-1">
                      <div className="h-full bg-green-600 rounded-full" style={{width: '25%'}}></div>
                    </div>
                    <p className="text-[10px] text-[#333] text-right">Protection level: <span className="text-green-600 font-bold">Low</span></p>
                  </div>
                </div>
                
                <div className="bg-[#fff8e1] border border-[#ffecb3] p-2 mb-4 rounded-md">
                  <div className="flex items-start justify-end">
                    <p className="text-[10px] text-[#333] text-right">
                      <span className="font-bold">WARNING:</span> Your digital behavior creates a unique psychological profile more accurate than your own self-perception. This profile is used to target your specific vulnerabilities.
                    </p>
                    <div className="text-[#f57c00] ml-2">⚠️</div>
                  </div>
                </div>
                
                <h3 className="text-[#003399] text-sm font-bold mb-2 text-right">Field Notes</h3>
                
                {manifestoExcerpts.map((excerpt, index) => (
                  <div key={index} className="bg-white border border-[#ddd] p-2 mb-2 rounded-md shadow-sm">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-[10px] text-[#666]">Accessed: {new Date().toLocaleDateString()}</div>
                      <h4 className="text-[#003399] text-xs font-bold">Entry #{excerpt.id}</h4>
                    </div>
                    <p className="text-[10px] text-[#333] leading-relaxed text-right">{excerpt.content}</p>
                  </div>
                ))}
                
                <div className="mt-4 flex flex-col md:flex-row justify-end gap-4">
                  <Link href="/shop" className="xp-button py-2 px-4 text-xs">
                    ACQUIRE NEURAL PROTECTION
                  </Link>
                  <Link href="/intel" className="xp-button py-2 px-4 text-xs">
                    ACCESS FULL INTELLIGENCE
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Status Bar */}
            <div className="xp-statusbar px-2 py-1 text-[10px] text-[#666] flex justify-between items-center">
              <div>Items: 5 fragments</div>
              <div>Security Level: ULTRAVIOLET</div>
            </div>
          </div>
        )}
        
        {/* Terminal Window */}
        {activeWindow === 'terminal' && (
          <div className="absolute top-16 left-8 md:left-32 w-full max-w-[calc(100%-32px)] md:max-w-[80%] lg:max-w-[500px] xp-window backdrop-filter backdrop-blur-md bg-black/40 border border-gray-600/30 shadow-lg">
            {/* Window Header */}
            <div className="xp-window-header flex justify-between items-center backdrop-filter backdrop-blur-md bg-gradient-to-r from-gray-800/60 to-gray-700/60 text-white">
              <div className="flex items-center">
                <span className="mr-1">💻</span>
                Terminal - Neural Interface
              </div>
              <div className="flex gap-1">
                <button className="w-5 h-5 bg-[#333]/70 rounded-sm flex items-center justify-center text-[10px] hover:bg-[#333] text-white" onClick={() => minimizeWindow('terminal')}>_</button>
                <button className="w-5 h-5 bg-[#333]/70 rounded-sm flex items-center justify-center text-[10px] hover:bg-[#333] text-white" onClick={() => toggleWindow('terminal')}>✕</button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 h-[300px] overflow-y-auto backdrop-filter backdrop-blur-sm bg-black/40 font-mono text-green-500 text-sm">
              <div 
                ref={terminalRef}
                className="h-full overflow-y-auto"
              >
                <div className="mb-2">dongOS Enterprise Edition 2025 [Version 5.1.2600]</div>
                <div className="mb-2">E:\\DAN\SYSTEM\NewDongAccords&gt; </div>
                {terminalText}
                {isTyping && <span className="inline-block w-2 h-4 bg-green-500 ml-1 animate-pulse">|</span>}
              </div>
            </div>
          </div>
        )}
        
        {/* Documents Window */}
        {activeWindow === 'documents' && (
          <div className="absolute top-20 left-12 md:left-40 w-full max-w-[calc(100%-32px)] md:max-w-[80%] lg:max-w-[500px] xp-window backdrop-filter backdrop-blur-md bg-[#ece9d8]/20 border border-[#ece9d8]/30 shadow-lg">
            {/* Window Header */}
            <div className="xp-window-header flex justify-between items-center backdrop-filter backdrop-blur-md bg-gradient-to-r from-blue-600/50 to-blue-500/50 text-white">
              <div className="flex items-center">
                <span className="mr-1">📁</span>
                Documents - Pain Matrix Files
              </div>
              <div className="flex gap-1">
                <button className="w-5 h-5 bg-[#ece9d8]/70 rounded-sm flex items-center justify-center text-[10px] hover:bg-[#ece9d8] text-gray-800" onClick={() => minimizeWindow('documents')}>_</button>
                <button className="w-5 h-5 bg-[#ece9d8]/70 rounded-sm flex items-center justify-center text-[10px] hover:bg-[#ece9d8] text-gray-800" onClick={() => toggleWindow('documents')}>✕</button>
              </div>
            </div>
            
            {/* Toolbar */}
            <div className="xp-toolbar flex justify-end gap-2 backdrop-filter backdrop-blur-sm bg-[#ece9d8]/40">
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">File</button>
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">Edit</button>
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">View</button>
              <button className="xp-button bg-[#ece9d8]/60 hover:bg-[#ece9d8]">Tools</button>
            </div>
            
            {/* Content */}
            <div className="xp-window-content p-4 h-[300px] overflow-y-auto backdrop-filter backdrop-blur-sm bg-white/20 text-gray-800">
              <div className="flex flex-col gap-1">
                {[3721, 3722, 3723, 3724, 3725, 3726, 3727, 3728, 3729, 3730, 3731, 3732, 3733, 3734, 3735].map((id) => (
                  <div key={id} className="flex items-center justify-end p-1 hover:bg-[#f0f0f0] cursor-pointer rounded-sm">
                    <div className="text-xs text-black">FRAGMENT_{id}.txt</div>
                    <div className="w-5 h-5 ml-2 text-xs">📄</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Status Bar */}
            <div className="xp-statusbar px-2 py-1 text-[10px] text-[#666] flex justify-between items-center">
              <div>15 items</div>
              <div>CLASSIFIED</div>
            </div>
          </div>
        )}
        
        {/* Notifications */}
        <div className="absolute bottom-12 right-2 flex flex-col gap-2 z-20">
          {notifications.map((notification, index) => (
            <div key={index} className="xp-notification p-2 animate-[xp-notification_0.3s_ease-in-out]">
              <div className="flex items-center gap-2">
                <div className="text-sm">🔔</div>
                <div className="text-xs">{notification}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Taskbar - fixed at the bottom */}
      <div className="h-10 bg-gradient-to-r from-[#0a0a0a] to-[#1a1a1a] border-t border-[#333] flex items-center justify-between z-20 absolute bottom-0 left-0 right-0">
        <div className="flex items-center h-full">
          <button 
            className="h-full px-3 flex items-center bg-gradient-to-b from-[#2a5ebb] to-[#5188c8] text-white font-bold text-sm border-r border-[#0a0a0a]"
            onClick={() => setShowStartMenu(!showStartMenu)}
          >
            <span className="mr-1">🔒</span> Start
          </button>
          
          {/* Minimized Windows */}
          <div className="flex h-full">
            {minimized.includes('main') && (
              <div 
                className="h-full px-3 flex items-center bg-[#333] text-white text-xs border-r border-[#0a0a0a] cursor-pointer"
                onClick={() => toggleWindow('main')}
              >
                <span className="mr-1">🔒</span> Pain Matrix Analysis
              </div>
            )}
            
            {minimized.includes('terminal') && (
              <div 
                className="h-full px-3 flex items-center bg-[#333] text-white text-xs border-r border-[#0a0a0a] cursor-pointer"
                onClick={() => toggleWindow('terminal')}
              >
                <span className="mr-1">C:</span> Command Prompt
              </div>
            )}
            
            {minimized.includes('documents') && (
              <div 
                className="h-full px-3 flex items-center bg-[#333] text-white text-xs border-r border-[#0a0a0a] cursor-pointer"
                onClick={() => toggleWindow('documents')}
              >
                <span className="mr-1">📁</span> Documents
              </div>
            )}
          </div>
        </div>
        
        {/* System Tray */}
        <div className="flex items-center gap-2 px-2">
          {/* Notifications */}
          {notifications.map((notification, index) => (
            <div key={index} className="bg-[#333] text-white px-2 py-1 text-[10px] rounded-sm animate-pulse">
              {notification}
            </div>
          ))}
          
          <div className="bg-[#333] text-white px-2 py-1 text-[10px] rounded-sm">
            {currentTime.toLocaleTimeString()}
          </div>
        </div>
      </div>
      
      {/* Start Menu */}
      {showStartMenu && (
        <div className="absolute bottom-10 left-0 w-64 bg-white border border-[#0e2a36] shadow-xl z-20 max-h-[calc(100%-20px)] overflow-auto">
          <div className="bg-gradient-to-b from-[#2a5ebb] to-[#5188c8] h-12 flex items-center px-3">
            <div className="text-yellow-100 font-bold">Pain Matrix</div>
          </div>
          
          <div className="flex">
            <div className="w-3/4 p-2">
              <div 
                className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm"
                onClick={() => toggleWindow('main')}
              >
                <div className="w-6 h-6 mr-2 bg-white rounded-sm flex items-center justify-center text-red-600 text-xs font-bold">DP</div>
                <div className="text-sm">Pain Matrix Analysis</div>
              </div>
              
              <div 
                className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm"
                onClick={() => toggleWindow('terminal')}
              >
                <div className="w-6 h-6 mr-2 bg-black rounded-sm flex items-center justify-center text-green-500 text-xs">C:</div>
                <div className="text-sm">Command Prompt</div>
              </div>
              
              <div 
                className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm"
                onClick={() => toggleWindow('documents')}
              >
                <div className="w-6 h-6 mr-2 bg-yellow-100 rounded-sm flex items-center justify-center text-yellow-600 text-xs">📁</div>
                <div className="text-sm">Documents</div>
              </div>
              
              <div className="border-t border-[#ddd] my-2"></div>
              
              <Link href="/shop" className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm">
                <div className="w-6 h-6 mr-2 bg-blue-100 rounded-sm flex items-center justify-center text-blue-600 text-xs">🛡️</div>
                <div className="text-sm">Neural Protection</div>
              </Link>
              
              <Link href="/intel" className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm">
                <div className="w-6 h-6 mr-2 bg-blue-100 rounded-sm flex items-center justify-center text-blue-600 text-xs">🔍</div>
                <div className="text-sm">Intelligence Access</div>
              </Link>
              
              <div className="border-t border-[#ddd] my-2"></div>
              
              <div className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm">
                <div className="w-6 h-6 mr-2 bg-gray-200 rounded-sm flex items-center justify-center text-gray-600 text-xs">🔄</div>
                <div className="text-sm">Restart</div>
              </div>
              
              <div className="flex items-center p-2 hover:bg-[#3a70c1] hover:text-white cursor-pointer rounded-sm">
                <div className="w-6 h-6 mr-2 bg-gray-200 rounded-sm flex items-center justify-center text-gray-600 text-xs">⏻</div>
                <div className="text-sm">Shut Down</div>
              </div>
            </div>
            
            <div className="w-1/4 bg-[#d3e5fa] h-full border-l border-[#ddd]">
              <div className="p-2">
                <div className="text-xs text-[#003399] font-bold mb-2">User</div>
                <div className="w-10 h-10 bg-[#3a70c1] rounded-sm mb-4"></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PainMatrixXPImproved; 